/* eslint-disable @typescript-eslint/lines-between-class-members */
/* eslint-disable class-methods-use-this */
import * as React from 'react';
import {
  CellTemplate,
  Cell,
  Compatible,
  Uncertain,
  getCellProperty,
  keyCodes,
} from '@silevis/reactgrid';
import { Box, IconButton, Spinner } from '@chakra-ui/react';

import { usePlanRevampTable } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/PlanRevampTableContext';

const AddIndicatorPlusIcon = ({ color = '#000000' }: { color?: string }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={16}
    height={16}
    viewBox='0 0 14 14'
    fill='none'
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <path d='M7 2.5V11.5M2.5 7H11.5' stroke={color} strokeWidth='1.5' strokeLinecap='round' />
  </svg>
);

const AddRowCellContent = ({
  cell,
  planPermission,
  isProcessing,
  onAddRow,
}: {
  cell: Compatible<FlagCell>;
  planPermission?: boolean;
  isProcessing?: boolean;
  onAddRow: () => void;
}) => {
  const isRevampTable = usePlanRevampTable();
  const plusColor = isRevampTable ? '#8B5CF6' : '#000000';

  return (
    <Box
      cursor={planPermission ? 'not-allowed' : 'pointer'}
      display='flex'
      alignItems='center'
      justifyContent={isRevampTable ? 'center' : 'flex-start'}
      width='100%'
      height='100%'
      px={isRevampTable ? 0 : '11px'}
      onClick={planPermission || isProcessing ? undefined : onAddRow}
    >
      <IconButton
        aria-label='add row'
        size='sm'
        variant='ghost'
        minW='28px'
        h='28px'
        cursor={planPermission ? 'not-allowed' : 'pointer'}
        icon={isProcessing ? <Spinner size='sm' /> : <AddIndicatorPlusIcon color={plusColor} />}
        _hover={{ bg: isRevampTable ? '#F5F0FF' : undefined }}
      />
      {cell.showText && (isProcessing ? 'Adding...' : 'Add indicator')}
    </Box>
  );
};

export interface FlagCell extends Cell {
  type: 'flag';
  text: string;
  rowId: string;
  showText?: boolean;
  placeholder?: string;
  planPermission?: boolean;
  isProcessing?: boolean;
  validator?: (text: string) => boolean;
}

export class AddRowCellTemplate implements CellTemplate<FlagCell> {
  onAddRow: () => void;
  planPermission?: boolean;
  isProcessing?: boolean;

  constructor(onAddRow: () => void, planPermission: boolean, isProcessing?: boolean) {
    this.onAddRow = onAddRow;
    this.planPermission = planPermission;
    this.isProcessing = isProcessing;
  }

  getCompatibleCell(uncertainCell: Uncertain<FlagCell>): Compatible<FlagCell> {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    const value = Number.parseFloat(text);
    return { ...uncertainCell, text, value } as Compatible<FlagCell>;
  }

  handleKeyDown(
    cell: Compatible<FlagCell>,
    keyCode: number,
  ): { cell: Compatible<FlagCell>; enableEditMode: boolean } {
    if (keyCode === keyCodes.ENTER) {
      return { cell, enableEditMode: true };
    }
    return { cell, enableEditMode: false };
  }

  getClassName(cell: Compatible<FlagCell>): string {
    const isValid = cell.validator ? cell.validator(cell.text) : true;
    const className = cell.className || '';
    return `${className} ${isValid ? 'valid' : 'invalid'} ${
      cell.placeholder && cell.text === '' ? 'placeholder' : ''
    }`;
  }

  render(cell: Compatible<FlagCell>) {
    return (
      <AddRowCellContent
        cell={cell}
        planPermission={this.planPermission}
        isProcessing={this.isProcessing}
        onAddRow={this.onAddRow}
      />
    );
  }
}
